import { Shift } from '../types/schedule.model'
import { dayjs, toDateKey } from '../utils/schedule'
import {
  EmployeeShiftColorDto,
  ScheduleWeekDto,
  UpsertScheduleWeekDto,
  UpsertShiftDto
} from './ScheduleAPI.model'
import { DEFAULT_SHIFT_TYPE, isShiftType } from '../types/shiftType.model'

/** Shifts of the week starting at `weekStart`, in a stable order. */
export const shiftsInWeek = (shifts: Shift[], weekStart: string): Shift[] => {
  const start = dayjs(weekStart)
  const end = start.add(7, 'day')

  return shifts
    .filter(shift => {
      const day = dayjs(shift.date)
      return !day.isBefore(start, 'day') && day.isBefore(end, 'day')
    })
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.startMinutes - b.startMinutes ||
        a.userId.localeCompare(b.userId)
    )
}

export const storeToWeekDto = (
  shifts: Shift[],
  colorsByUserId: Record<string, string>,
  weekStart: string
): UpsertScheduleWeekDto => {
  const colors = Object.entries(colorsByUserId).map(
    ([userId, color]): EmployeeShiftColorDto => ({ userId, color })
  )

  return {
    weekStart,
    shifts: shiftsInWeek(shifts, weekStart).map((shift): UpsertShiftDto => ({
      // `remoteId` is set only once the backend has stored this shift. A
      // local id must never be sent, it would ask the server to update a
      // row that does not exist.
      id: shift.remoteId,
      userId: shift.userId,
      date: shift.date,
      startMinutes: shift.startMinutes,
      endMinutes: shift.endMinutes,
      // Always sent, including the default. A PUT replaces the week, so an
      // omitted type would have the server fall back on its own default rather
      // than on what is on screen.
      type: shift.type ?? DEFAULT_SHIFT_TYPE,
      ...(shift.note ? { note: shift.note } : {})
    })),
    // An empty array would read as "this restaurant has no colours" and wipe
    // them; absent means "leave them as they are".
    ...(colors.length ? { colors } : {})
  }
}

export const weekDtoToStore = (
  week: ScheduleWeekDto
): { shifts: Shift[]; colorsByUserId: Record<string, string> } => ({
  shifts: (week.shifts ?? []).map(dto => ({
    // The contract guarantees an id on every returned shift, and it doubles as
    // the React key here. No derived fallback: one built from the shift's own
    // fields collides whenever the same person has two shifts starting at the
    // same minute, which the UI allows.
    id: dto.id,
    remoteId: dto.id,
    userId: dto.userId,
    date: dto.date,
    startMinutes: dto.startMinutes,
    endMinutes: dto.endMinutes,
    // Guarded rather than cast: a value this client does not know, from a newer
    // server or a hand-edited row, reads as work instead of breaking the grid.
    type: isShiftType(dto.type) ? dto.type : DEFAULT_SHIFT_TYPE,
    note: dto.note
  })),
  colorsByUserId: (week.colors ?? []).reduce<Record<string, string>>(
    (acc, entry) => {
      acc[entry.userId] = entry.color
      return acc
    },
    {}
  )
})

/**
 * What is genuinely week-scoped, for the per-week dirty check.
 *
 * Colours are deliberately NOT part of this. They are restaurant-wide, the
 * same map travels with every week, so including them made one colour change
 * differ from the saved snapshot of EVERY loaded week at once, and the saver
 * answered with a full PUT per week. A month in the payroll view is five or
 * six of them; a browsed range is more. See `serializeColors`.
 */
export const serializeWeek = (shifts: Shift[], weekStart: string): string =>
  JSON.stringify({
    weekStart,
    shifts: shiftsInWeek(shifts, weekStart).map(shift => ({
      userId: shift.userId,
      date: shift.date,
      startMinutes: shift.startMinutes,
      endMinutes: shift.endMinutes,
      // Always sent, including the default. A PUT replaces the week, so an
      // omitted type would have the server fall back on its own default rather
      // than on what is on screen.
      type: shift.type ?? DEFAULT_SHIFT_TYPE,
      ...(shift.note ? { note: shift.note } : {})
    }))
  })

/**
 * The restaurant-wide half, tracked on its own.
 *
 * Every week's PUT carries the whole colour map, so persisting a change needs
 * exactly one request, whichever week it rides along with.
 */
export const serializeColors = (
  colorsByUserId: Record<string, string>
): string =>
  JSON.stringify(
    Object.entries(colorsByUserId)
      .map(([userId, color]) => ({ userId, color }))
      .sort((a, b) => a.userId.localeCompare(b.userId))
  )

export const weekStartOf = (date: string): string =>
  toDateKey(dayjs(date).startOf('isoWeek'))
